import React from 'react'

// Order numbers in `orders.order_number`, all still live: `ORD-02461247`
// (from 2026-09-19), `ORD-XXXXXXXX-XXXX`, `ORD-XXXXXX`, `ORD-V2-<32 hex>` and
// `ORD-V2-P-<32 hex>`. Any number of further segments, so the three-segment
// purchase numbers link whole instead of stopping at `ORD-V2-P`. Anchored to
// word boundaries so `XORD-…` etc. doesn't match.
export const ORDER_NUMBER_RE = /\bORD(?:-[A-Z0-9]+)+\b/g

/**
 * Find every order number in `text` and wrap it in an anchor.
 * `hrefFor` decides where the click goes — different surfaces route to
 * different orders lists (operator console, seller dashboard, buyer
 * profile) since each viewer reads order detail in a different place.
 *
 * Returns the original string when nothing matches so callers can drop it
 * into JSX without a wrapping fragment.
 */
export function linkifyOrderNumbers(
  text: string,
  hrefFor: (orderNumber: string) => string,
  options?: { inverse?: boolean },
): React.ReactNode {
  if (!text) return text
  const inverse = options?.inverse === true
  // A link inside a sentence is a link: accent colour and an underline, in the
  // surrounding typeface. It used to be a monospace pill on a tinted fill with
  // matching tinted text, which broke the line rhythm of every message it
  // appeared in and read as generic dashboard styling rather than prose.
  const linkStyle: React.CSSProperties = inverse
    ? { color: '#fff', textDecorationColor: 'rgba(255,255,255,0.5)' }
    : { color: 'var(--primary, #0a84ff)', textDecorationColor: 'currentColor' }
  const re = new RegExp(ORDER_NUMBER_RE.source, 'g')
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let i = 0
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index))
    const n = match[0]
    parts.push(
      <a
        key={`ord-${i++}-${match.index}`}
        href={hrefFor(n)}
        onClick={(e) => e.stopPropagation()}
        className="[overflow-wrap:anywhere]"
        style={{
          fontWeight: 500,
          textDecoration: 'underline',
          textUnderlineOffset: 2,
          ...linkStyle,
        }}
      >
        {n}
      </a>,
    )
    lastIndex = match.index + n.length
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex))
  return parts.length > 0 ? parts : text
}
